<template>
  <div class="page-main">
    <div class="page-header">
      <ol class="breadcrumb">
        <li class="breadcrumb-item active">Admin</li>
      </ol>
      <h1 class="page-title">Credit Transactions</h1>
    </div>
    <div class="page-content">
      <div class="panel">
        <div class="panel-body panel-form">
          <loading-indicator v-if="isLoading"></loading-indicator>
          <div v-else-if="isError">
          </div>
          <div v-else>
            <div class="p-b-15">
              <form v-on:submit.prevent="filterItems" autocomplete="off">
                <div class="row">
                  <div class="col-xs-3 col-md-2">
                    <div class="form-group">
                      <label class="form-control-label">Month</label>
                      <select class="form-control" v-model="currentMonth">
                        <option value="">Show all</option>
                        <option v-for="(month, index) in monthList" v-bind:value="month">{{month}}</option>
                      </select>
                    </div>
                  </div>
                  <div class="col-xs-3 col-md-2">
                    <div class="form-group">
                      <label class="form-control-label">Source Type</label>
                      <select class="form-control" v-model="currentSourceType">
                        <option value="">Show all</option>
                        <option v-for="(value, key, index) in sourceTypeList" v-bind:value="key">{{value}}</option>
                      </select>
                    </div>
                  </div>
                  <div class="col-xs-3 col-md-2">
                    <div class="form-group">
                      <label class="form-control-label">Credit Type</label>
                      <select class="form-control" v-model="currentCreditType">
                        <option value="">Show all</option>
                        <option value="add">Add</option>
                        <option value="deduct">Deduct</option>
                      </select>
                    </div>
                  </div>
                  <div class="col-xs-3 col-md-2">
                    <button type="submit" class="btn btn-success m-t-35" :disabled="isSubmiting" v-bind:class="{ 'btn-default': isSubmiting }">
                      <div class="loader vertical-align-middle loader-circle loader-form" v-if="isSubmiting"></div> Filter
                    </button>
                  </div>
                </div>
              </form>
            </div>
            <div class="table-striped table-hover table-responsive" v-if="total_transaction">
              <table class="table">
                <thead>
                  <tr>
                    <th class="cell-120">Date</th>
                    <th class="cell-120" scope="col">Order Number</th>
                    <th class="cell-120" scope="col">Invoice Number</th>
                    <th class="cell-120" scope="col">Receipt Number</th>
                    <th class="cell-120" scope="col">Team</th>
                    <th class="cell-250" scope="col">Description</th>
                    <th class="cell-100" scope="col">Amount</th>
                    <th class="cell-100 text-xs-right" scope="col"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, index) in transactions" v-bind:id="item.id">
                    <td class="cell-120">
                      <span :title="getFullHistoryDate(item.created_at)">{{getFullHistoryDate(item.created_at)}}</span>
                    </td>
                    <td class="cell-120">
                      <router-link v-bind:to="urlOrderNumberReport(item)" class="btn btn-link" v-if="item.statement && item.statement.order_number"> {{ item.statement.order_number }} </router-link>
                      <span v-else class="text-xs-center">-</span>
                    </td>
                    <td class="cell-120">
                      <span v-if="item.statement && item.statement.invoice_number">{{item.statement.invoice_number}}</span>
                      <span v-else class="text-xs-center">-</span>
                    </td>
                    <td class="cell-120">
                      <span v-if="item.statement && item.statement.receipt_number">{{item.statement.receipt_number}}</span>
                      <span v-else class="text-xs-center">-</span>
                    </td>
                    <td class="cell-120">
                      <router-link
                        v-bind:to="`/${$route.params.namespace}/admin/teams/${item.team.namespace}/billings`"
                        alt="Manage billing" class="btn btn-link">{{item.team.name}}
                      </router-link>
                    </td>
                    <td class="cell-250">
                      <p class="m-b-0">{{item.source}}</p>
                      <p class="m-b-0 font-size-12 grey-500">{{item.description}} <span v-if="item.note">{{item.note}}</span>
                      </p>
                    </td>
                    <td class="cell-100">
                      <span class="green-600" v-if="item.credit > 0">{{item.human_credit}} {{ $t("billing.currencies.thb") }}</span>
                      <span class="red-600" v-else-if="item.credit < 0">
                        {{item.human_credit}} {{ $t("billing.currencies.thb") }}
                      </span>
                      <span v-else>{{item.human_credit}} {{ $t("billing.currencies.thb") }}</span>
                    </td>
                    <td class="cell-100 text-xs-right">
                      <a class="btn btn-sm btn-icon btn-default" :href="`${item.statement.download_url}`"
                      v-if="item.statement && item.statement.download_url" download>
                        Download
                      </a>
                      <router-link v-bind:to="urlUsageReport(item)" class="btn btn-sm btn-icon btn-default" v-if="showUsageReport(item.statement)"> Usage Report
                      </router-link>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="alert alert-primary alert-dismissible" v-else>
              There is no transaction history.
            </div>
          </div>
          <paginate
            v-show="(pagination.total_page > 1)"
            v-bind:pageCount="pagination.total_page"
            v-bind:prevText="'«'"
            v-bind:nextText="'»'"
            v-bind:clickHandler="changePage"
            v-bind:containerClass="'pagination pull-right'"
            v-bind:pageClass="'page-item'"
            v-bind:pageLinkClass="'page-link'"
            v-bind:prevClass="'page-item'"
            v-bind:prevLinkClass="'page-link'"
            v-bind:nextClass="'page-item'"
            v-bind:nextLinkClass="'page-link'">
          </paginate>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import moment from 'moment';
import { mapGetters } from 'vuex';
import LoadingIndicator from 'components/LoadingIndicator';

export default {
  components: {
    LoadingIndicator,
  },
  data: function() {
    return {
      isSubmiting: false,
      isLoading: false,
      isError: false,
      currentMonth: '',
      currentSourceType: '',
      currentCreditType: '',
      total_transaction: 0,
      transactions: [],
      sourceTypeList: [],
      pagination: {
        total: 0,
        per_page: 20,
        current_page: 1,
        last_page: 0,
        from: 1,
        to: 1,
        next_page_url: null,
        prev_page_url: null,
        total_page: 0,
      },
    };
  },
  computed: {
    ...mapGetters([
      'currentUser',
    ]),
    team: function() {
      return this.currentUser.team;
    },
    monthList: function() {
      const list = [];
      const team = this.team;
      if (team.created_at) {
        const now = moment().startOf('months');
        const start = moment(team.created_at).startOf('months');
        const months = (now.diff(start, 'months'));
        list.push(now.format('MMMM YYYY'));

        for (let i = 0; i < months; i += 1) {
          list.push(now.subtract(1, 'months').format('MMMM YYYY'));
        }
      }

      return list;
    },
  },
  created: function() {
    this.isLoading = true;
  },
  mounted: function() {
    this.fetchTransactions();
    this.fetchSourceTypes();
  },
  methods: {
    fetchTransactions: function(page) {
      let url = `/api/admin/transactions`;
      let hasFirstFiler = false;

      if (this.currentSourceType) {
        if (hasFirstFiler) {
          url = `${url}&source_type=${this.currentSourceType}`;
        } else {
          hasFirstFiler = true;
          url = `${url}?source_type=${this.currentSourceType}`;
        }
      }

      if (this.currentMonth) {
        const month = moment(this.currentMonth, ['MMMM YYYY', moment.ISO_8601]).format('YYYY-MM');
        if (hasFirstFiler) {
          url = `${url}&month=${month}`;
        } else {
          hasFirstFiler = true;
          url = `${url}?month=${month}`;
        }
      }

      if (this.currentCreditType) {
        if (hasFirstFiler) {
          url = `${url}&credit_type=${this.currentCreditType}`;
        } else {
          hasFirstFiler = true;
          url = `${url}?credit_type=${this.currentCreditType}`;
        }
      }

      if (page) {
        if (hasFirstFiler) {
          url = `${url}&page=${page}`;
        } else {
          hasFirstFiler = true;
          url = `${url}?page=${page}`;
        }
      }

      axios.get(url)
      .then((response) => {
        this.total_transaction = response.data.total;
        this.makePagination(response.data);
        this.transactions = response.data.data;
        this.isLoading = false;
        this.isError = false;
        this.isSubmiting = false;
      })
      .catch(() => {
        this.isLoading = false;
        this.isError = true;
        this.isSubmiting = false;
      });
    },
    fetchSourceTypes: function() {
      const url = `/api/sourcetypes`;

      axios.get(url)
      .then((response) => {
        this.sourceTypeList = response.data;
        this.isLoading = false;
        this.isError = false;
      })
      .catch(() => {
        this.isLoading = false;
        this.isError = true;
      });
    },
    makePagination: function(data) {
      this.pagination.total = data.total;
      this.pagination.per_page = data.per_page;
      this.pagination.current_page = data.current_page;
      this.pagination.last_page = data.last_page;
      this.pagination.from = data.from;
      this.pagination.to = data.to;
      this.pagination.next_page_url = data.next_page_url;
      this.pagination.prev_page_url = data.prev_page_url;
      this.pagination.total_page = Math.ceil(data.total / data.per_page);
    },
    changePage: function(page) {
      this.fetchTransactions(page);
    },
    getHistoryDate: function (date) {
      return moment(date, ['YYYY-MM-DD', moment.ISO_8601]).format('D MMM YYYY');
    },
    getFullHistoryDate: function (date) {
      return moment(date, ['YYYY-MM-DD HH:mm', moment.ISO_8601]).format('D MMM YYYY HH:mm');
    },
    filterItems: function () {
      this.fetchTransactions();
    },
    showUsageReport: function (statement) {
      if (!statement) {
        return false;
      }

      if (statement.source_type === 'System') {
        return true;
      }

      return false;
    },
    urlUsageReport: function (item) {
      if (item.statement && item.statement.month) {
        const month = moment(item.statement.month, ['YYYY-MM-DD HH:mm', moment.ISO_8601]).format('YYYY-MM');

        const parentUrl = this.$route.params.namespace;
        const namespace = item.team.namespace;

        const url = `/${parentUrl}/admin/teams/${namespace}/usage-report`;

        return `${url}?month=${month}`;
      }

      return '';
    },
    urlOrderNumberReport: function (item) {
      if (item.statement && item.statement.order_number) {
        const parentUrl = this.$route.params.namespace;
        const namespace = item.team.namespace;

        const url = `/${parentUrl}/admin/teams/${namespace}/transactions`;

        return `${url}?number=${item.statement.order_number}`;
      }
      return '';
    },
  },
};
</script>

<style scoped>
</style>



// WEBPACK FOOTER //
// CreditTransactionBrowser.vue?22ee9750